// AdminEventsHubPage — unified Admin > Events command center.
//
// Phase E1.2 (Feb 26, 2026): "Mission Control" UI/UX cleanup.
//   - Top stats strip (Active / Open / In Progress / Completed / Archived)
//   - Row actions: primary "Manage" + three-dot overflow menu
//     (Archive / Delete permanently) — less visual noise
//   - Cleaner type/status pills, stronger event-name hierarchy
//   - Reset filters button when filters are active
//   - Context-aware empty states (no events / no matches / no archived / no QA)
//   - Mobile cards mirror the same hierarchy
//
// Phase E1.1 (Feb 26, 2026): admin full-rights — delete or archive any
// event at any lifecycle stage; QA toggle; status & type filters.
// Phase E1   (Feb 25, 2026): unified hub for Tournaments + Court Movement.
//
// No event mechanics or backend changes in E1.2.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Plus, Gear, Trash, MagnifyingGlass, Funnel, Archive, Flask, Tag,
  CalendarBlank, Clock, CheckCircle, Lightning, DotsThreeVertical, ArrowCounterClockwise,
  Prohibit, ArrowClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { eventAPI } from '../lib/eventApi';
import { tournamentAPI, TOURNAMENT_STATUS_LABELS } from '../lib/tournamentApi';
import { roundRobinAPI, RR_STATUS_LABELS } from '../lib/roundRobinApi';
import { adminAPI } from '../lib/api';
import { EVENT_STATUS_LABEL } from '../lib/eventsCatalog';
import ConfirmDialog from '../components/ConfirmDialog';
import { clubEventsAPI } from '../lib/clubEventsApi';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../components/ui/dropdown-menu';

const EVENT_TYPES = [
  { value: '',            label: 'All event types' },
  { value: 'ladder',      label: 'Ladder' },
  { value: 'tournament',  label: 'Tournament' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'session',     label: 'Court Movement' },
  { value: 'club_event',  label: 'Social Event' },
];

const STATUS_FILTERS = [
  { value: '',                label: 'All statuses' },
  { value: 'active_always_open', label: 'Active · Always Open' },
  { value: 'open',            label: 'Open (registration)' },
  { value: 'in_progress',     label: 'In progress' },
  { value: 'completed',       label: 'Completed' },
  { value: 'archived',        label: 'Archived' },
];

const TYPE_BADGE = {
  tournament:  'bg-[#fffbf0] text-[#c9a93a] border-[#c9a93a]/40',
  round_robin: 'bg-[#f0f7f2] text-[#015e3f] border-[#015e3f]/30',
  session:     'bg-[#f3f1ec] text-[#013523] border-[#e3dcd1]',
  ladder:      'bg-[#f0f7f2] text-[#015e3f] border-[#015e3f]/40',
  club_event:  'bg-[#f4efe6] text-[#7c5b1a] border-[#c9a93a]/40',
};

const TYPE_LABEL = {
  tournament:  'Tournament',
  round_robin: 'Round Robin',
  session:     'Court Movement',
  ladder:      'Ladder',
  club_event:  'Social Event',
};

// Display-only status simplification (E2). Underlying lifecycle values are
// unchanged in the DB — this only maps them to the owner's four primary
// labels (+ Closed / Archived / Cancelled where they genuinely apply).
function primaryStatusLabel(status) {
  switch (status) {
    case 'draft':                return 'Created';
    case 'active':
    case 'active_always_open':
    case 'registration_open':    return 'Open';
    case 'closed':
    case 'registration_closed':  return 'Closed';
    case 'bracket_generated':
    case 'in_progress':          return 'In-Progress';
    case 'completed':            return 'Completed';
    case 'archived':             return 'Archived';
    case 'cancelled':            return 'Cancelled';
    default:                     return status;
  }
}

const TOURNAMENT_TYPE_LABEL = { singles: 'Singles', doubles: 'Doubles' };

const SESSION_STATUS_LABEL = {
  ...EVENT_STATUS_LABEL,
  draft: 'Draft',
  registration_open: 'Registration open',
  registration_closed: 'Registration closed',
  in_progress: 'In progress',
  completed: 'Completed',
  archived: 'Archived',
  cancelled: 'Cancelled',
};

function statusPillClass(status) {
  switch (status) {
    case 'active_always_open':   return 'bg-[#f0f7f2] text-[#015e3f] border-[#015e3f]/40';
    case 'active':               return 'bg-[#f0f7f2] text-[#015e3f] border-[#015e3f]/40';
    case 'closed':               return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'draft':                return 'bg-[#f3f1ec] text-[#6a766e] border-[#e3dcd1]';
    case 'registration_open':    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'registration_closed':  return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'bracket_generated':    return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'in_progress':          return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'completed':            return 'bg-stone-100 text-stone-700 border-stone-300';
    case 'archived':             return 'bg-zinc-100 text-zinc-500 border-zinc-300';
    case 'cancelled':            return 'bg-rose-50 text-rose-700 border-rose-200';
    default:                     return 'bg-[#f3f1ec] text-[#6a766e] border-[#e3dcd1]';
  }
}

function statusBucket(status) {
  if (status === 'active_always_open')                                         return 'active_always_open';
  if (status === 'active' || status === 'closed')                              return 'open';
  if (['draft', 'registration_open', 'registration_closed'].includes(status)) return 'open';
  if (['bracket_generated', 'in_progress'].includes(status))                   return 'in_progress';
  if (status === 'completed')                                                  return 'completed';
  if (['archived', 'cancelled'].includes(status))                              return 'archived';
  return status;
}

const QA_TEST_PATTERN = /^(qa\s*-|test\b|pytest|demo|smoke|slice|reopen|verify)/i;
const isQaEvent = (name) => !!name && QA_TEST_PATTERN.test(name.trim());

// "Open registration" = registration is genuinely open right now. Excludes
// drafts, closed, registration_closed and every terminal state. Kept separate
// from statusBucket() so status display/filter mappings stay unchanged.
const OPEN_REGISTRATION_STATUSES = ['registration_open', 'active', 'active_always_open'];
const isOpenRegistration = (status) => OPEN_REGISTRATION_STATUSES.includes(status);
const ladderGenderLabel = (gender) =>
  gender === 'mens' ? "Men's" : gender === 'womens' ? 'Ladies' : 'Open';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
}

// Top stats strip — one card per high-level bucket. Active = open + in_progress.
function StatsStrip({ counts }) {
  const cards = [
    { key: 'active',      label: 'Active events',    value: counts.active,     icon: Lightning,     accent: 'text-[#013523]' },
    { key: 'open',        label: 'Open reg.',        value: counts.openReg,     icon: CalendarBlank, accent: 'text-emerald-700' },
    { key: 'in_progress', label: 'In progress',      value: counts.in_progress, icon: Clock,         accent: 'text-indigo-700' },
    { key: 'completed',   label: 'Completed',         value: counts.completed,   icon: CheckCircle,   accent: 'text-stone-700' },
    { key: 'archived',    label: 'Archived',          value: counts.archived,    icon: Archive,       accent: 'text-zinc-500' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="hub-stats-strip">
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={`hub-stat-${c.key}`}
          className="border border-[#e3dcd1] rounded-2xl bg-white px-4 py-3 flex items-center gap-3 hover:border-[#013523] transition-colors"
        >
          <c.icon size={22} className={`${c.accent} shrink-0`} weight="duotone" />
          <div className="min-w-0">
            <div className={`text-2xl font-semibold tabular-nums ${c.accent}`} data-testid={`hub-stat-${c.key}-value`}>
              {c.value}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-[#6a766e] truncate">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Row overflow menu — status-aware lifecycle actions.
// Ladders now behave like every other event: Close / Reopen (ladder-only,
// passed via onClose/onReopen), Archive / Restore, and permanent Delete.
function RowActionsMenu({ row, onClose, onReopen, onArchive, onRestore, onDelete }) {
  if (row.never_delete) return null;
  const isArchived = row.status === 'archived';
  const isClosed = row.status === 'closed';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`hub-row-menu-${row.id}`}
          className="px-2 py-1 rounded-md text-[#6a766e] hover:bg-[#f3f1ec] inline-flex items-center"
          title="More actions">
          <DotsThreeVertical size={16} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {onClose && !isArchived && !isClosed && (
          <DropdownMenuItem
            data-testid={`hub-close-${row.id}`}
            onSelect={() => onClose(row)}
            className="cursor-pointer">
            <Prohibit size={14} className="mr-2 text-amber-700" /> Close to new players
          </DropdownMenuItem>
        )}
        {onReopen && isClosed && (
          <DropdownMenuItem
            data-testid={`hub-reopen-${row.id}`}
            onSelect={() => onReopen(row)}
            className="cursor-pointer">
            <ArrowClockwise size={14} className="mr-2 text-[#015e3f]" /> Reopen participation
          </DropdownMenuItem>
        )}
        {!isArchived && (
          <DropdownMenuItem
            data-testid={`hub-archive-${row.id}`}
            onSelect={() => onArchive(row)}
            className="cursor-pointer">
            <Archive size={14} className="mr-2 text-[#6a766e]" /> Archive event
          </DropdownMenuItem>
        )}
        {isArchived && (
          <DropdownMenuItem
            data-testid={`hub-restore-${row.id}`}
            onSelect={() => onRestore(row)}
            className="cursor-pointer">
            <ArrowCounterClockwise size={14} className="mr-2 text-[#015e3f]" /> Restore event
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          data-testid={`hub-delete-${row.id}`}
          onSelect={() => onDelete(row)}
          className="cursor-pointer text-red-700 focus:text-red-700 focus:bg-red-50">
          <Trash size={14} className="mr-2" /> Delete permanently
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AdminEventsHubPage() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [rrs, setRrs] = useState(null);
  const [ladders, setLadders] = useState(null);
  const [clubEvents, setClubEvents] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [onlyQa, setOnlyQa] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    tournamentAPI.list().then((r) => setTournaments(r.data || [])).catch(() => setTournaments([]));
    eventAPI.listSessions().then((r) => setSessions(r.data?.sessions || [])).catch(() => setSessions([]));
    roundRobinAPI.list().then((r) => setRrs(r.data || [])).catch(() => setRrs([]));
    // Phase L-ADMIN1: pull the always-open ladders so they appear as first-class
    // events in the hub with a Manage action.
    adminAPI.listLadders().then((r) => setLadders(r.data?.ladders || [])).catch(() => setLadders([]));
    // Feb 2026 — Club Events (lightweight, no registration).
    clubEventsAPI.list({ include_archived: true }).then((r) => setClubEvents(r.data?.club_events || [])).catch(() => setClubEvents([]));
  };
  useEffect(() => { refresh(); }, []);

  const rows = useMemo(() => {
    const out = [];
    // Phase L-ADMIN1: ladders appear as always-open events at the top.
    // Ranked before other event types so admins can always see & manage them.
    (ladders || []).forEach((l) => out.push({
      kind: 'ladder',
      id: l.slug,
      name: l.name,
      type_label: `${ladderGenderLabel(l.gender)} Singles Ladder`,
      status: l.status || 'active',
      status_label: l.status === 'archived' ? 'Archived'
                  : l.status === 'closed' ? 'Closed to new players'
                  : 'Active · Open',
      created_at: l.created_at || null,
      sort_key: '9999',  // pin ladders to the top of the default sort
      meta: `${ladderGenderLabel(l.gender)} · ${l.player_count || 0} player${l.player_count === 1 ? '' : 's'}`,
      manage_to: `/admin/ladders/${l.slug}/manage`,
    }));
    (tournaments || []).forEach((t) => out.push({
      kind: 'tournament',
      id: t.id,
      name: t.name,
      type_label: TOURNAMENT_TYPE_LABEL[t.tournament_type] || t.tournament_type,
      status: t.status,
      status_label: TOURNAMENT_STATUS_LABELS[t.status] || t.status,
      created_at: t.created_at,
      meta: `${t.entry_count} ${t.entry_count === 1 ? 'entry' : 'entries'}${t.bracket_size ? ` · ${t.bracket_size}-slot` : ''}`,
      manage_to: `/tournaments/${t.id}/manage`,
      featured_on_home: !!t.featured_on_home,
    }));
    (sessions || []).forEach((s) => out.push({
      kind: 'session',
      id: s.id,
      name: s.name,
      type_label: (s.event_slug || s.gender || 'Court Movement').replace(/_/g, ' '),
      status: s.status,
      status_label: SESSION_STATUS_LABEL[s.status] || s.status,
      created_at: s.created_at,
      meta: `${s.num_courts || '?'} courts · ${s.weeks_planned || '?'} weeks${s.gender ? ` · ${ladderGenderLabel(s.gender)}` : ''}`,
      manage_to: `/admin/events/${s.id}`,
    }));
    (rrs || []).forEach((r) => out.push({
      kind: 'round_robin',
      id: r.id,
      name: r.name,
      type_label: `${r.rr_type === 'singles' ? 'Singles' : 'Doubles'} · ${r.num_pools} pool${r.num_pools > 1 ? 's' : ''}`,
      status: r.status,
      status_label: RR_STATUS_LABELS[r.status] || r.status,
      created_at: r.created_at,
      meta: `${r.entry_count || 0} ${r.entry_count === 1 ? 'entry' : 'entries'}${r.has_final ? ' · final enabled' : ''}`,
      manage_to: `/round-robins/${r.id}/manage`,
      featured_on_home: !!r.featured_on_home,
    }));
    (clubEvents || []).forEach((c) => out.push({
      kind: 'club_event',
      id: c.id,
      name: c.name,
      type_label: 'Club Event',
      status: c.status,
      status_label: c.status === 'archived' ? 'Archived' : 'Active',
      created_at: c.created_at,
      meta: [c.location_text, c.starts_at ? new Date(c.starts_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null]
              .filter(Boolean).join(' · ') || '—',
      manage_to: `/admin/club-events/${c.id}`,
      featured_on_home: !!c.featured_on_home,
      never_delete: false,
    }));
    return out.sort((a, b) => ((b.sort_key || b.created_at || '').localeCompare(a.sort_key || a.created_at || '')));
  }, [tournaments, sessions, rrs, ladders, clubEvents]);

  // Bucket counts feed the stats strip + filter chip badges.
  const counts = useMemo(() => {
    const c = { open: 0, openReg: 0, in_progress: 0, completed: 0, archived: 0, qa: 0 };
    rows.forEach((r) => {
      const b = statusBucket(r.status);
      c[b] = (c[b] || 0) + 1;
      if (r.kind !== 'club_event' && isOpenRegistration(r.status)) c.openReg += 1;
      if (isQaEvent(r.name)) c.qa += 1;
    });
    c.active = c.open + c.in_progress;
    return c;
  }, [rows]);

  if (!user) return <div className="text-sm text-[#6a766e]">Loading…</div>;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const filtered = rows.filter((r) => {
    if (typeFilter && r.kind !== typeFilter) return false;
    if (statusFilter && statusBucket(r.status) !== statusFilter) return false;
    if (onlyQa && !isQaEvent(r.name)) return false;
    if (!search.trim()) return true;
    const t = search.toLowerCase();
    return r.name.toLowerCase().includes(t) || (r.type_label || '').toLowerCase().includes(t);
  });

  const handleConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.action === 'delete') {
        if (confirm.kind === 'tournament')       await tournamentAPI.remove(confirm.id);
        else if (confirm.kind === 'round_robin') await roundRobinAPI.remove(confirm.id);
        else if (confirm.kind === 'club_event')  await clubEventsAPI.remove(confirm.id);
        else if (confirm.kind === 'ladder')      await adminAPI.deleteLadder(confirm.id);
        else                                     await eventAPI.deleteSession(confirm.id);
        toast.success(`${confirm.name} deleted.`);
      } else if (confirm.action === 'archive') {
        if (confirm.kind === 'tournament')       await tournamentAPI.update(confirm.id, { status: 'archived' });
        else if (confirm.kind === 'round_robin') await roundRobinAPI.update(confirm.id, { status: 'archived' });
        else if (confirm.kind === 'club_event')  await clubEventsAPI.archive(confirm.id);
        else if (confirm.kind === 'ladder')      await adminAPI.archiveLadder(confirm.id);
        else                                     await eventAPI.archiveSession(confirm.id);
        toast.success(`${confirm.name} archived.`);
      } else if (confirm.action === 'restore') {
        if (confirm.kind === 'tournament')       await tournamentAPI.restore(confirm.id);
        else if (confirm.kind === 'round_robin') await roundRobinAPI.restore(confirm.id);
        else if (confirm.kind === 'club_event')  await clubEventsAPI.restore(confirm.id);
        else if (confirm.kind === 'ladder')      await adminAPI.restoreLadder(confirm.id);
        else                                     await eventAPI.restoreSession(confirm.id);
        toast.success(`${confirm.name} restored.`);
      } else if (confirm.action === 'close') {
        await adminAPI.closeLadder(confirm.id);
        toast.success(`${confirm.name} closed to new players.`);
      } else if (confirm.action === 'reopen') {
        await adminAPI.reopenLadder(confirm.id);
        toast.success(`${confirm.name} reopened.`);
      }
      setConfirm(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || `Could not ${confirm.action}.`);
    } finally { setBusy(false); }
  };

  const loading = tournaments === null || sessions === null || rrs === null;
  const hasActiveFilters = !!typeFilter || !!statusFilter || onlyQa || !!search.trim();
  const resetFilters = () => { setSearch(''); setTypeFilter(''); setStatusFilter(''); setOnlyQa(false); };

  // Context-aware empty-state copy + CTA.
  const emptyState = (() => {
    if (rows.length === 0) {
      return {
        title: 'No events yet',
        body: 'Create your first tournament or Court Movement session to get rolling.',
        cta: null,
      };
    }
    if (onlyQa && counts.qa === 0) {
      return {
        title: 'No QA / test events',
        body: 'You\'re clean — no events here look like QA fixtures (names starting with "QA -", "Test", etc.).',
        cta: { label: 'Show all events', action: resetFilters },
      };
    }
    if (statusFilter === 'archived' && counts.archived === 0) {
      return {
        title: 'No archived events',
        body: 'Nothing in the archive yet. Archive an event from the row menu when you want to preserve its history without keeping it in the active list.',
        cta: { label: 'Show all events', action: resetFilters },
      };
    }
    return {
      title: 'Nothing matches your filters',
      body: 'Try adjusting search, type, or status — or reset all filters to see everything.',
      cta: { label: 'Reset filters', action: resetFilters },
    };
  })();

  const confirmTitle = confirm
    ? (confirm.action === 'archive'
        ? `Archive "${confirm.name}"?`
        : confirm.action === 'restore'
          ? `Restore "${confirm.name}"?`
          : confirm.action === 'close'
            ? `Close "${confirm.name}" to new players?`
            : confirm.action === 'reopen'
              ? `Reopen "${confirm.name}"?`
              : `Permanently delete "${confirm.name}"?`)
    : '';
  const confirmMsg = confirm
    ? (confirm.action === 'archive'
        ? 'The event will be moved to the archive. All registrations, matches, and history are kept and stay accessible to admins. The event will be hidden from member-facing pages. You can still hard-delete later.'
        : confirm.action === 'restore'
          ? 'The event will be moved out of the archive and restored to its previous state (active or closed — whichever it was before archiving). All of its registrations, scores, and history are preserved exactly as they were.'
          : confirm.action === 'close'
            ? 'Members can no longer join this ladder or start new challenges. Existing players, rankings, and in-flight challenges stay exactly as they are, and the standings remain visible. You can reopen it anytime.'
            : confirm.action === 'reopen'
              ? 'Members will be able to join this ladder and start new challenges again. Rankings and history are unchanged.'
              : confirm.kind === 'ladder'
                ? `This permanently removes "${confirm.name}" and ALL of its players, rankings, challenges, match results, and movement history. Members' profiles, their records on other ladders, and unrelated events are left untouched. This cannot be undone and the ladder will NOT reappear.`
                : `This permanently removes "${confirm.name}" and ALL of its registrations, matches, weeks, courts, scores, rankings, and history. This cannot be undone and the event will NOT reappear. For in-progress or completed events, "Archive" is usually the safer choice.`)
    : '';

  return (
    <div className="space-y-6" data-testid="admin-events-hub">
      {/* Header + Actions */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight">Admin · Events</h1>
          <p className="text-sm text-[#6a766e] mt-1">
            Your command center for every event the club runs — Tournaments, Court Movement, and more.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            data-testid="hub-new-event-btn"
            to="/admin/events/new"
            className="px-4 py-2 rounded-full bg-[#013523] text-white text-sm flex items-center gap-1.5 hover:bg-[#024029] transition-colors">
            <Plus size={16} /> New Event
          </Link>
        </div>
      </div>

      {/* Stats Strip */}
      {!loading && <StatsStrip counts={counts} />}

      {/* Filter bar */}
      <div className="border border-[#e3dcd1] rounded-2xl bg-[#fbfaf8] p-3">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
            <input
              data-testid="hub-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events by name or type…"
              className="w-full pl-9 pr-3 py-2 border border-[#e3dcd1] rounded-full text-sm bg-white focus:outline-none focus:border-[#013523]" />
          </div>
          <div className="relative">
            <Funnel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
            <select
              data-testid="hub-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="pl-8 pr-7 py-2 border border-[#e3dcd1] rounded-full text-sm bg-white focus:outline-none focus:border-[#013523]">
              {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
            <select
              data-testid="hub-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-8 pr-7 py-2 border border-[#e3dcd1] rounded-full text-sm bg-white focus:outline-none focus:border-[#013523]">
              {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <button
            data-testid="hub-qa-toggle"
            onClick={() => setOnlyQa((v) => !v)}
            className={`px-3 py-2 rounded-full text-xs flex items-center gap-1.5 border transition-colors ${
              onlyQa
                ? 'bg-[#013523] text-white border-[#013523]'
                : 'bg-white text-[#6a766e] border-[#e3dcd1] hover:border-[#013523]'
            }`}
            title="Only show QA / test events">
            <Flask size={12} weight={onlyQa ? 'fill' : 'regular'} />
            QA / test
            <span className={`ml-0.5 text-[10px] tabular-nums ${onlyQa ? 'text-white/80' : 'text-[#9aa39d]'}`}>
              {counts.qa}
            </span>
          </button>
          {hasActiveFilters && (
            <button
              data-testid="hub-clear-filters"
              onClick={resetFilters}
              className="px-3 py-2 rounded-full text-xs flex items-center gap-1 bg-white text-[#013523] border border-[#013523]/30 hover:border-[#013523] transition-colors">
              Reset filters
            </button>
          )}
        </div>
        <div className="mt-2 text-xs text-[#6a766e]" data-testid="hub-counts">
          Showing <span className="font-semibold text-[#013523] tabular-nums">{filtered.length}</span> of {rows.length} events
          {hasActiveFilters && <span> · filters active</span>}
        </div>
      </div>

      {loading && <div className="text-sm text-[#6a766e]" data-testid="hub-loading">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-[#e3dcd1] rounded-2xl p-12 text-center bg-[#fbfaf8]" data-testid="hub-empty">
          <div className="inline-flex w-12 h-12 rounded-full bg-white border border-[#e3dcd1] items-center justify-center mb-3">
            <CalendarBlank size={22} className="text-[#013523]" weight="duotone" />
          </div>
          <div className="text-base text-[#013523] font-medium" data-testid="hub-empty-title">{emptyState.title}</div>
          <div className="text-sm text-[#6a766e] mt-1 max-w-md mx-auto" data-testid="hub-empty-body">{emptyState.body}</div>
          {emptyState.cta && (
            <button
              data-testid="hub-empty-clear-filters"
              onClick={emptyState.cta.action}
              className="mt-4 px-4 py-2 rounded-full bg-[#013523] text-white text-xs hover:bg-[#024029] transition-colors">
              {emptyState.cta.label}
            </button>
          )}
        </div>
      )}

      {/* Desktop table */}
      {filtered.length > 0 && (
        <div className="hidden sm:block overflow-hidden border border-[#e3dcd1] rounded-2xl bg-white">
          <table className="w-full text-sm" data-testid="hub-table">
            <thead className="bg-[#fbfaf8] text-[#6a766e] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Event</th>
                <th className="text-left px-3 py-3">Type</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const qa = isQaEvent(r.name);
                const archived = r.status === 'archived';
                return (
                  <tr
                    key={`${r.kind}-${r.id}`}
                    className={`border-t border-[#ece6dc] ${archived ? 'opacity-60' : ''} ${qa ? 'bg-[#fdfaf2]' : ''} hover:bg-[#fbfaf8]/60 transition-colors`}
                    data-testid={`hub-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        {qa && (
                          <span
                            data-testid={`hub-qa-flag-${r.id}`}
                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fffbf0] text-[#c9a93a] border border-[#c9a93a]/40 shrink-0 mt-0.5"
                            title="Looks like a QA / test event">QA</span>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-[#013523] truncate" title={r.name}>{r.name}</div>
                          <div className="text-[11px] text-[#9aa39d] truncate">{r.meta}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_BADGE[r.kind]}`}>
                          {TYPE_LABEL[r.kind]}
                        </span>
                        {r.featured_on_home && (
                          <span
                            data-testid={`hub-featured-badge-${r.id}`}
                            title="This event is featured on the Home page"
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200 font-semibold"
                          >
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        data-testid={`hub-status-pill-${r.id}`}
                        className={`text-[10px] px-2 py-0.5 rounded-full border ${statusPillClass(r.status)}`}>
                        {primaryStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#6a766e] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={r.manage_to}
                          data-testid={`hub-manage-${r.id}`}
                          className="px-3 py-1.5 rounded-full text-xs bg-[#013523] text-white hover:bg-[#024029] flex items-center gap-1 transition-colors">
                          <Gear size={12} /> Manage
                        </Link>
                        <RowActionsMenu
                          row={r}
                          onClose={r.kind === 'ladder' ? (row) => setConfirm({ action: 'close', ...row }) : undefined}
                          onReopen={r.kind === 'ladder' ? (row) => setConfirm({ action: 'reopen', ...row }) : undefined}
                          onArchive={(row) => setConfirm({ action: 'archive', ...row })}
                          onRestore={(row) => setConfirm({ action: 'restore', ...row })}
                          onDelete={(row) => setConfirm({ action: 'delete', ...row })}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {filtered.map((r) => {
          const qa = isQaEvent(r.name);
          const archived = r.status === 'archived';
          return (
            <div
              key={`${r.kind}-${r.id}`}
              className={`border rounded-2xl p-3 bg-white ${archived ? 'opacity-60 border-zinc-300' : 'border-[#e3dcd1]'} ${qa ? 'bg-[#fdfaf2]' : ''}`}
              data-testid={`hub-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  {qa && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fffbf0] text-[#c9a93a] border border-[#c9a93a]/40 shrink-0 mt-0.5">QA</span>
                  )}
                  <div className="min-w-0">
                    <div className="font-semibold text-[#013523] truncate">{r.name}</div>
                    <div className="text-[11px] text-[#9aa39d] truncate">{r.meta}</div>
                  </div>
                </div>
                <RowActionsMenu
                  row={r}
                  onClose={r.kind === 'ladder' ? (row) => setConfirm({ action: 'close', ...row }) : undefined}
                  onReopen={r.kind === 'ladder' ? (row) => setConfirm({ action: 'reopen', ...row }) : undefined}
                  onArchive={(row) => setConfirm({ action: 'archive', ...row })}
                  onRestore={(row) => setConfirm({ action: 'restore', ...row })}
                  onDelete={(row) => setConfirm({ action: 'delete', ...row })}
                />
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_BADGE[r.kind]}`}>
                  {TYPE_LABEL[r.kind]}
                </span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${statusPillClass(r.status)}`}>
                  {primaryStatusLabel(r.status)}
                </span>
                {r.featured_on_home && (
                  <span
                    data-testid={`hub-featured-badge-card-${r.id}`}
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200 font-semibold"
                  >
                    Featured
                  </span>
                )}
                <span className="text-xs text-[#9aa39d]">{fmtDate(r.created_at)}</span>
              </div>
              <div className="mt-3">
                <Link
                  to={r.manage_to}
                  data-testid={`hub-manage-mob-${r.id}`}
                  className="block text-center px-3 py-2 rounded-full bg-[#013523] text-white text-xs hover:bg-[#024029] transition-colors">
                  Manage
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!confirm}
        title={confirmTitle}
        message={confirmMsg}
        confirmLabel={(() => {
          const a = confirm?.action;
          const map = {
            archive: ['Archiving…', 'Archive event'],
            restore: ['Restoring…', 'Restore event'],
            close: ['Closing…', 'Close to new players'],
            reopen: ['Reopening…', 'Reopen participation'],
            delete: ['Deleting…', 'Delete permanently'],
          };
          const [busyLabel, label] = map[a] || map.delete;
          return busy ? busyLabel : label;
        })()}
        destructive={confirm?.action === 'delete'}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
